import React, {useState, useContext, useEffect} from "react";
import {azureTokenContext, userInfoContext, messageBoxContext, userIdContext} from "../App";
import {targetUserIdContext} from "./MainPart";
import Loading from "./Serving/Loading";
import Contracts from "./Serving/Contracts";
import LinkToMainPage from "./Serving/LinkToMainPage";

import {useNavigate} from "react-router-dom";

import * as ApiCalls from "./Serving/ApiCalls";
import {validateNorwegianBankAccount} from "../utils/validateNorwegianBankAccount";

import "../style/editProfile.css";

import {Container, TextField, Grid, CircularProgress} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import UnresolvedValidationsBox from "./Serving/UnresolvedValidationsBox";
import {UserInfoType} from "../Types/UserInfo";
import {useLogMetric} from "../hooks/useLogMetric";

export default function EditProfile() {
    const azureToken = useContext(azureTokenContext);
    const [userInfo, setUserInfo] = useContext(userInfoContext);
    const userId = useContext(userIdContext);
    const targetUserContext = useContext(targetUserIdContext);
    const targetUserId = Array.isArray(targetUserContext) ? targetUserContext[0] : null;
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [newProfilePicture, setNewProfilePicture] = useState(null);
    const [newImageLocation, setNewImageLocation] = useState<string | null>(null);
    const logMetric = useLogMetric();
    const [isChanged, setIsChanged] = useState(false);
    const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
    const serverUserRef = React.useRef<UserInfoType>({});

    interface Errors {
        Name?: string;
        Email?: string;
        Phone?: string;
        SocialSecurityNumber?: string;
        BankAccountNumber?: string;
    }

    const [errors, setErrors] = useState<Errors>({});

    const [newUserInfo, setNewUserInfo] = useState<UserInfoType>({});

    const navigate = useNavigate();

    const setMessageBoxProp = useContext(messageBoxContext);

    // Prevent editing another user's profile
    useEffect(() => {
        if (targetUserId && userId && targetUserId !== userId) {
            navigate("/profile");
        }
    }, [targetUserId, userId, navigate]);

    useEffect(() => {
        const checkData = async () => {
            log("INFO", `Navigated to EditProfile page`, "AuthorsPortal", "", "");

            fillNewUserDataInfo(userInfo);

            if (userInfo.Id) {
                // Fetch actual server values (without validation overlay) so that
                // handleSubmit doesn't re-submit pending validation field values.
                try {
                    const serverUser = await ApiCalls.getUserInfo(userInfo.Id, azureToken);
                    serverUserRef.current = serverUser;
                } catch {
                    serverUserRef.current = userInfo;
                }

                if (!hasSensitiveInfo()) {
                    await addSensitiveInfoToUserInfo(userInfo);
                }
                setLoading(false);
            }
        };

        checkData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo.Id]);

    const hasSensitiveInfo = () => {
        return userInfo.SocialSecurityNumber || userInfo.BankAccountNumber;
    };

    const addSensitiveInfoToUserInfo = async (updatedUser) => {
        var newUserInfo = updatedUser ? updatedUser : userInfo;
        await ApiCalls.GetUserSensitiveInfo(userInfo, azureToken)
            .then((res) => {
                newUserInfo = {
                    ...newUserInfo,
                    SocialSecurityNumber: res.SocialSecurityNumber,
                    BankAccountNumber: res.BankAccountNumber,
                };
                setUserInfo(newUserInfo);

                fillNewUserDataInfo(newUserInfo);
            })
            .catch((e) => {
                setLoading(false);
                setMessageBoxProp("FailedGetUserSensitiveInfo");
                log("ERROR", "Failed GetUserSensitiveInfo " + e.message, "AuthorsPortal", "", "");
            });
    };

    const handleChange = (e) => {
        const {name, value} = e.target;

        setIsChanged(true);
        setChangedFields(prev => new Set(prev).add(name));

        setNewUserInfo((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleFileInputChange = (event) => {
        if (event.target.files[0]) {
            setIsChanged(true);
            const file = event.target.files[0];
            setNewProfilePicture(file);
            setNewImageLocation(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async () => {
        // For validation fields (Email, Employer, ImageUrl, Title, Description),
        // use the server-confirmed value unless the user explicitly changed it.
        // This prevents pending validation overlay values from being re-submitted.
        const server = serverUserRef.current;
        const val = (field: string, formValue: any, serverValue: any) =>
            changedFields.has(field) ? formValue : serverValue;

        const formedData = {
            ActorUserId: userInfo.Id,
            Id: userInfo.Id,
            Name: newUserInfo.Name ?? userInfo.Name,
            Email: val('Email', newUserInfo.Email ?? userInfo.Email, server.Email),
            Phone: newUserInfo.Phone ?? userInfo.Phone,
            UserSensitiveInfo: {
                SocialSecurityNumber: clearNumbers(newUserInfo.SocialSecurityNumber) ?? userInfo.SocialSecurityNumber,
                BankAccountNumber: clearNumbers(newUserInfo.BankAccountNumber) ?? userInfo.BankAccountNumber,
            },
            Address: newUserInfo.Address ?? userInfo.Address,
            ZipCode: newUserInfo.ZipCode ?? userInfo.ZipCode,
            City: newUserInfo.City ?? userInfo.City,
            Country: newUserInfo.Country ?? userInfo.Country,
            Employer: val('Employer', newUserInfo.Employer ?? userInfo.Employer, server.Employer),
            Title: val('Title', newUserInfo.Title ?? userInfo.Title, server.Title),
            Description: val('Description', newUserInfo.Description ?? userInfo.Description, server.Description),
            ImageUrl: server.ImageUrl,
        };

        if (newProfilePicture) {
            //upload new file and then change newUserInfo.imageUrl
            await ApiCalls.uploadUserProfile(userInfo, newProfilePicture, azureToken)
                .then(async (res) => {
                    formedData.ImageUrl = res.Url;

                    log("INFO", "Uploaded profile picture successful.", "AuthorsPortal", "", "");
                    await updateUserInfo(formedData);
                })
                .catch((err) => {
                    log("ERROR", "Failed uploading profile picture " + err.message, "AuthorsPortal", "", "");
                    setMessageBoxPropFunc("FailedEditUserInfo");
                });
        } else {
            await updateUserInfo(formedData);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (validateFields()) {
            setSaving(true);
            await handleSubmit().then(() => {
                resetData();
            });
        }
    };

    const resetData = () => {
        setSaving(false);
        setIsChanged(false);
        setChangedFields(new Set());
        setErrors({});
        fillNewUserDataInfo(userInfo);
        setNewProfilePicture(null);
        setNewImageLocation(null);
    };

    const updateUserInfo = async (formedData) => {
        await ApiCalls.updateUserInfo(formedData, azureToken)
            .then(async (res) => {
                serverUserRef.current = res.UpdatedUser;
                setUserInfo(() => ({...res.UpdatedUser}));
                const newUserInfo = await addUnresolvedUserInfo(res.UpdatedUser);
                setUserInfo(newUserInfo);
                addSensitiveInfoToUserInfo(newUserInfo);
                log("INFO", `Edited user info ${userInfo.Name} : ${userInfo.Id}`, "AuthorsPortal", "", "");
                logMetric({category: "Profile", subCategory: "Updated", message: `User updated profile info`});
                if (res.WentToManualValidation === true) {
                    setMessageBoxPropFunc("WentToManualValidationEditUserInfo");
                } else {
                    setMessageBoxPropFunc("SuccessEditUserInfo");
                }
                navigate("/");
            })
            .catch((e) => {
                log(
                    "ERROR",
                    `Failed to edit user info. Name: "${userInfo.Name}", ID: "${userInfo.Id}". Error: ${e.message}`,
                    "AuthorsPortal",
                    "",
                    ""
                );
                logMetric({category: "Profile", subCategory: "Update failed", message: `Failed to update profile info`, status: "Failed"});
                setMessageBoxPropFunc("FailedEditUserInfo");
            });
    };

    const setMessageBoxPropFunc = (msg) => {
        // since it is invocated by changes in prop, I want to make sure that it changes
        setMessageBoxProp("");
        setTimeout(() => {
            setMessageBoxProp(msg);
        }, 10);
    };

    const fillNewUserDataInfo = (req) => {
        setNewUserInfo({
            Id: req.Id,
            Name: req.Name,
            Email: req.Email,
            Phone: req.Phone,
            SocialSecurityNumber: req.SocialSecurityNumber,
            BankAccountNumber: req.BankAccountNumber,
            Address: req.Address,
            ZipCode: req.ZipCode,
            City: req.City,
            Country: req.Country,
            Employer: req.Employer,
            Title: req.Title,
            Description: req.Description,
            ImageUrl: req.ImageUrl,
        });
    };

    const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const validateEmail = (email: string | undefined): string | undefined => {
        if (!email || !email.trim()) return "E-post er påkrevd";
        if (!EMAIL_REGEX.test(email.trim())) return "Ugyldig e-postadresse";
        return undefined;
    };

    const validateField = (name: string) => {
        setErrors((prev) => {
            const next = {...prev};
            switch (name) {
                case "Name":
                    if (!newUserInfo.Name) next.Name = "Navn er påkrevd";
                    else delete next.Name;
                    break;
                case "Email": {
                    const emailError = validateEmail(newUserInfo.Email);
                    if (emailError) next.Email = emailError;
                    else delete next.Email;
                    break;
                }
                case "Phone":
                    if (newUserInfo.Phone && !/^\+?\d{8,15}$/.test(newUserInfo.Phone))
                        next.Phone = "Ugyldig mobilnummer";
                    else delete next.Phone;
                    break;
                case "SocialSecurityNumber":
                    if (newUserInfo.SocialSecurityNumber && !/^\d{11}$/.test(clearNumbers(newUserInfo.SocialSecurityNumber)))
                        next.SocialSecurityNumber = "Fødselsnummer må ha 11 sifre";
                    else delete next.SocialSecurityNumber;
                    break;
                case "BankAccountNumber":
                    if (newUserInfo.BankAccountNumber && !/^\d{11}$/.test(clearNumbers(newUserInfo.BankAccountNumber)))
                        next.BankAccountNumber = "Kontonummer må ha 11 sifre";
                    else delete next.BankAccountNumber;
                    break;
            }
            return next;
        });
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateField(e.target.name);
    };

    const validateFields = () => {
        const tempErrors = {} as Errors;

        if (!newUserInfo.Name) tempErrors.Name = "Navn er påkrevd";

        const emailError = validateEmail(newUserInfo.Email);
        if (emailError) tempErrors.Email = emailError;

        if (newUserInfo.Phone && !/^\+?\d{8,15}$/.test(newUserInfo.Phone)) {
            tempErrors.Phone = "Ugyldig mobilnummer";
        }
        if (newUserInfo.SocialSecurityNumber && !/^\d{11}$/.test(clearNumbers(newUserInfo.SocialSecurityNumber))) {
            tempErrors.SocialSecurityNumber = "Fødselsnummer må ha 11 sifre";
        }

        if (newUserInfo.BankAccountNumber && !validateNorwegianBankAccount(newUserInfo.BankAccountNumber)) {
            tempErrors.BankAccountNumber = "Kontonummer er ikke gyldig";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const addUnresolvedUserInfo = async (baseUserInfo) => {
        return await ApiCalls.getUnresolvedValidations(baseUserInfo, azureToken)
            .then((res) => {
                if (res.length === 0) return baseUserInfo;

                const sortedRes = [...res].sort((a, b) =>
                    new Date(b.CreatedUtc).getTime() - new Date(a.CreatedUtc).getTime()
                );

                // Merge all pending validations — newest value wins per field
                const merged: Record<string, string> = {};
                for (const v of sortedRes) {
                    if (v.UserId !== baseUserInfo?.Id) continue;
                    if (v.DescriptionChanged && !('Description' in merged)) merged.Description = v.Description;
                    if (v.EmailChanged && !('Email' in merged)) merged.Email = v.Email;
                    if (v.EmployerChanged && !('Employer' in merged)) merged.Employer = v.Employer;
                    if (v.ImageUrlChanged && !('ImageUrl' in merged)) merged.ImageUrl = v.ImageUrl;
                    if (v.TitleChanged && !('Title' in merged)) merged.Title = v.Title;
                }

                if (merged.ImageUrl) {
                    setNewImageLocation(merged.ImageUrl);
                }

                return {...baseUserInfo, ...merged};
            })
            .catch((e) => {
                log("ERROR", "Failed GetUnresolvedValidation " + e.message, "Author portal", "", "");
                return baseUserInfo;
            });
    };

    const clearNumbers = (bankAccountNumber) => {
        return bankAccountNumber ? bankAccountNumber.replace(/[.\s-]/g, "") : "";
    };

    const log = (level, message, method, workItemId, userRole) => {
        ApiCalls.log(level, message, method, workItemId, userRole, userInfo.Id, azureToken);
    };

    const styleMUITextFields = {
        backgroundColor: "#f5f5f5",
        "& .MuiOutlinedInput-root": {
            "& fieldset": {
                borderColor: "#ccc",
            },
            "&:hover fieldset": {
                borderColor: "#999",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#fed506",
            },
        },
        borderRadius: "8px",
    };

    const styleMUIGrid = {
        padding: "9px 1px 9px 1px",
        backgroundColor: "#e9eeef",
    };

    return (
        <>
            {loading ? (
                <Loading/>
            ) : (
                <>
                    <LinkToMainPage/>

                    <Container className="editProfileMainContainer" maxWidth="xl">
                        <form onSubmit={handleSave}>
                            <Grid className="editProfileSubContainer" container spacing={1}>
                                <Grid item xs={12} md={12} lg={12}>
                                    <div className="profile-header-row">
                                        <div className="editProfileTitle">
                                            <div className="iconBackground">
                                                <PersonOutlineOutlinedIcon fontSize="large"/>
                                            </div>
                                            <div className="editProfileTitleText">Rediger profil</div>
                                        </div>

                                    </div>
                                </Grid>
                                <Grid item xs={12} md={12} lg={12}>
                                    <UnresolvedValidationsBox/>
                                </Grid>
                                <Grid item xs={12} md={4} lg={4}>
                                    <Grid container>
                                        <Grid item xs={12} md={11}>
                                            <div className="editProfileColumnHeader">Kontaktinformasjon</div>
                                        </Grid>

                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Navn"
                                                name="Name"
                                                value={newUserInfo.Name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                                error={!!errors.Name}
                                                helperText={errors.Name}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="E-post"
                                                name="Email"
                                                type="email"
                                                inputProps={{ inputMode: "email" }}
                                                value={newUserInfo.Email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                                error={!!errors.Email}
                                                helperText={errors.Email}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Mobilnummer"
                                                name="Phone"
                                                value={newUserInfo.Phone}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                                error={!!errors.Phone}
                                                helperText={errors.Phone}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Grid container>
                                        <Grid item xs={12} md={11}>
                                            <div className="editProfileColumnHeader">Forfatterprofil</div>
                                        </Grid>
                                        <Grid item xs={12} md={11}>
                                            <div>
                                                Rediger feltene under for å oppdatere din forfatterprofil som vises på
                                                karnovgroup.no/forfattere.
                                            </div>
                                            <div className="imageUploadingDiv">
                                                {newImageLocation ? (
                                                    <div className="imagePreviewDiv">
                                                        <img className="profileImage" src={newImageLocation}
                                                             alt="Profile"></img>
                                                    </div>
                                                ) : userInfo.ImageUrl && userInfo.ImageUrl.trim() !== '' ? (
                                                    <div className="imagePreviewDiv">
                                                        <img 
                                                            className="profileImage" 
                                                            src={userInfo.ImageUrl}
                                                            alt="Profile"
                                                            onError={(e) => {
                                                                e.currentTarget.onerror = null;
                                                                e.currentTarget.src = `${process.env.PUBLIC_URL}/no-profile-image.png`;
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="imagePreviewDiv">
                                                        <img 
                                                            className="profileImage" 
                                                            src={`${process.env.PUBLIC_URL}/no-profile-image.png`}
                                                            alt="Profile"
                                                        />
                                                    </div>
                                                )}
                                                <div className="changeProfilePictureDiv">
                                                    <input
                                                        type="file"
                                                        name="profilePicture"
                                                        accept="image/*"
                                                        onChange={handleFileInputChange}
                                                        className="hidden-file-input"
                                                        id="profilePictureInput"
                                                    />
                                                    <label className="changeProfilePictureButton"
                                                           htmlFor="profilePictureInput">
                                                        Endre bilde
                                                    </label>
                                                </div>

                                            </div>
                                            <div className="image-upload-note">
                                                Det kan ta opptil én uke før bildet blir synlig på
                                                forfatterprofilen.
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Tittel"
                                                name="Title"
                                                value={newUserInfo.Title}
                                                onChange={handleChange}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Arbeidssted"
                                                name="Employer"
                                                value={newUserInfo.Employer}
                                                onChange={handleChange}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Forfatterpresentasjon"
                                                name="Description"
                                                value={newUserInfo.Description}
                                                onChange={handleChange}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                                multiline
                                                rows={5}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Grid container>
                                        <Grid item xs={12} md={11}>
                                            <div className="editProfileColumnHeader">Honorar</div>
                                        </Grid>
                                        <Grid item xs={12} sx={styleMUIGrid}>
                                            <p>
                                                Hvis du skal motta honorar som privatperson, må du sørge for at feltene
                                                er korrekt utfylt. Skal honoraret
                                                utbetales til firma, send e-post til royalty@karnovgroup.no så vil vi
                                                sende fakturaopplysninger. Se
                                                oversikten over dine publikasjoner og hvor vi har registrert at det
                                                utbetales til nedenfor.
                                            </p>
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Postadresse (privat)"
                                                name="Address"
                                                value={newUserInfo.Address}
                                                onChange={handleChange}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Postnummer"
                                                name="ZipCode"
                                                value={newUserInfo.ZipCode}
                                                onChange={handleChange}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Sted"
                                                name="City"
                                                value={newUserInfo.City}
                                                onChange={handleChange}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Land"
                                                name="Country"
                                                value={newUserInfo.Country}
                                                onChange={handleChange}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Fødselsnummer"
                                                name="SocialSecurityNumber"
                                                value={newUserInfo.SocialSecurityNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                                error={!!errors.SocialSecurityNumber}
                                                helperText={errors.SocialSecurityNumber}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                            <TextField
                                                fullWidth
                                                label="Kontonummer"
                                                name="BankAccountNumber"
                                                value={newUserInfo.BankAccountNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                variant="outlined"
                                                sx={styleMUITextFields}
                                                error={!!errors.BankAccountNumber}
                                                helperText={errors.BankAccountNumber}
                                            />
                                        </Grid>
                                        {/*<Grid item xs={12} md={11} sx={styleMUIGrid}>*/}
                                        {/*  Dine personopplysninger blir behandlet i henhold til vår personvernerklæring og GDPR.*/}
                                        {/*</Grid>*/}
                                        <Contracts/>
                                    </Grid>
                                </Grid>
                                <button
                                    className="saveButton"
                                    type="submit"
                                    disabled={saving || !isChanged}
                                    title={!isChanged ? "Ingen endringer å lagre" : ""}
                                >
                                    {saving ?
                                        <CircularProgress size={24} color="inherit"/> : "Lagre"}
                                </button>
                            </Grid>
                        </form>
                    </Container>
                </>
            )}
        </>
    );
}
